import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Spin,
  Alert,
  Typography,
  Row,
  Col,
  Tag,
  Empty,
  Button,
  message,
  Divider,
  Pagination,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  fetchTreatmentPlanByPatient,
  TreatmentPlan,
} from "../../../api/treatmentPlan.api";
// ✅ 1. Importamos la API de Amortización y su tipo
import {
  fetchAmortizationsByPatient,
  //Amortization,
} from "../../../api/amortization.api";
import { getPacienteById, Paciente } from "../../../api/paciente.api";

const { Title, Text } = Typography;

interface TratamientosProps {
  pacienteId: number;
}

// ✅ Interface para nuestros datos financieros agregados
interface PlanFinancialSummary {
  treatmentName: string;
  amountPaid: number;
  pendingAmount: number;
  totalAmount: number;
  startDate: Date;
  endDate: Date;
}

const Tratamientos: React.FC<TratamientosProps> = ({ pacienteId }) => {
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  const [_paciente, setPaciente] = useState<Paciente | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);

  /** 🔁 Cargar datos del backend con la nueva lógica */
  const loadData = async () => {
    setLoading(true);
    try {
      const pacData = await getPacienteById(pacienteId);
      setPaciente(pacData);

      const codigoPaciente = Number(pacData.codigoPaciente);
      if (isNaN(codigoPaciente)) {
        throw new Error("Código de paciente inválido");
      }

      // ✅ 2. Traemos ambas listas: Planes (para la estructura) y Amortizaciones (para los datos)
      const [plansData, amortizationsData] = await Promise.all([
        fetchTreatmentPlanByPatient(codigoPaciente),
        fetchAmortizationsByPatient(codigoPaciente),
      ]);

      // ✅ 3. Agregamos los datos de amortización (tu idea)
      const financialMap = new Map<number, PlanFinancialSummary>();

      for (const amort of amortizationsData) {
        const id = amort.treatmentPlanId;
        const paymentDate = new Date(amort.paymentDate);

        if (!financialMap.has(id)) {
          // Si es la primera vez que vemos este plan, lo inicializamos
          financialMap.set(id, {
            treatmentName: amort.treatmentName || "Plan", // Usamos el nombre de la cuota
            amountPaid: 0,
            pendingAmount: 0,
            totalAmount: 0,
            startDate: paymentDate,
            endDate: paymentDate,
          });
        }

        const summary = financialMap.get(id)!;

        // Sumamos montos según estado
        if (amort.status.toLowerCase() === "paid") {
          summary.amountPaid += amort.amount;
        } else {
          // Asumimos que cualquier otro estado (pending, etc.) es pendiente
          summary.pendingAmount += amort.amount;
        }

        // Actualizamos fechas min/max
        if (paymentDate < summary.startDate) summary.startDate = paymentDate;
        if (paymentDate > summary.endDate) summary.endDate = paymentDate;

        // Aseguramos el nombre (por si la primera cuota era null)
        if (!summary.treatmentName && amort.treatmentName) {
          summary.treatmentName = amort.treatmentName;
        }
      }

      // Calculamos el total
      financialMap.forEach((summary) => {
        summary.totalAmount = summary.amountPaid + summary.pendingAmount;
      });

      // ✅ 4. Enriquecemos la lista de Planes con los datos financieros correctos
      const enrichedPlans = plansData.map((plan) => {
        const financialSummary = financialMap.get(plan.treatmentPlanId);

        // Si no encontramos datos financieros (un plan sin cuotas), devolvemos el plan original
        if (!financialSummary) {
          return plan;
        }

        // Si encontramos, REEMPLAZAMOS los datos incorrectos de la API de Planes
        // con los datos correctos que calculamos.
        return {
          ...plan, // Mantenemos 'variosTreatmens' y 'treatmentPlanId'
          treatmentName: financialSummary.treatmentName, // Nombre correcto
          totalAmount: financialSummary.totalAmount, // Total correcto
          amountPaid: financialSummary.amountPaid, // Pagado correcto
          pendingAmount: financialSummary.pendingAmount, // Pendiente correcto
          startDate: financialSummary.startDate.toISOString(), // Fecha inicio correcta
          endDate: financialSummary.endDate.toISOString(), // Fecha fin correcta
        };
      });

      // ✅ 5. ORDENAMOS los planes por fecha de fin (más reciente/futura primero)
      enrichedPlans.sort((a, b) => {
        // Usamos (as any) para acceder a 'endDate' que sabemos que existe
        const dateA = new Date((a as any).endDate || 0);
        const dateB = new Date((b as any).endDate || 0);
        // Orden descendente (B - A)
        return dateB.getTime() - dateA.getTime();
      });

      setPlans(enrichedPlans || []);
    } catch (err: unknown) {
      const messageText =
        err instanceof Error ? err.message : "Error cargando tratamientos";
      console.error("Error cargando tratamientos:", err);
      setError(messageText);
      message.error(messageText);
    } finally {
      setLoading(false);
    }
  };

  /** 🚀 Efecto inicial y refresco automático */
  useEffect(() => {
    loadData();

    const handleReload = () => setTimeout(() => loadData(), 800);
    document.addEventListener("visibilitychange", handleReload);
    window.addEventListener("focus", handleReload);
    window.addEventListener("pageshow", handleReload);

    return () => {
      document.removeEventListener("visibilitychange", handleReload);
      window.removeEventListener("focus", handleReload);
      window.removeEventListener("pageshow", handleReload);
    };
  }, [pacienteId]);

  /** 🔄 Refrescar manualmente */
  const handleRefresh = () => loadData();

  /** 📋 Columnas de tratamientos dentro de cada plan */
  const columns: ColumnsType<any> = [
    {
      title: "ID",
      dataIndex: "treatmentId",
      key: "treatmentId",
      width: 80,
      responsive: ["md"],
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) => text || "Sin descripción",
    },
    {
      title: "Precio",
      dataIndex: "itemPrice",
      key: "itemPrice",
      width: 100,
      render: (p) => (
        <Text strong style={{ color: "#1890ff" }}>
          ${p != null ? p.toFixed(2) : "0.00"}
        </Text>
      ),
    },
    {
      title: "Completado",
      dataIndex: "hasCheck",
      key: "hasCheck",
      width: 120,
      render: (checked) => (
        <Tag color={checked ? "success" : "warning"}>
          {checked ? "Sí" : "No"}
        </Tag>
      ),
    },
  ];

  /** 🧭 Paginación */
  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) setPageSize(newPageSize);
  };

  /** ⚠️ Error visual */
  if (error) return <Alert type="error" message={error} showIcon />;

  // Calculamos los items de la página actual
  const paginatedPlans = plans.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="treatments-container">
      <Card bordered={false} style={{ borderRadius: 8 }}>
        {/* Encabezado */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <Title level={3}>Planes de Tratamiento</Title>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Actualizar
          </Button>
        </div>

        {loading ? (
          <Spin size="large" tip="Cargando tratamientos..." />
        ) : plans.length > 0 ? (
          <>
            <Alert
              message={
                <>
                  <Text strong>Nota:</Text> Aquí puedes ver los tratamientos
                  incluidos en cada plan del paciente, junto con su estado y
                  montos asociados.
                </>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Divider />

            {paginatedPlans.map((plan, index) => (
              <Card
                key={plan.treatmentPlanId}
                className="treatment-plan-card"
                style={{
                  marginBottom: 24,
                  borderRadius: 8,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                }}
                title={`Plan #${(currentPage - 1) * pageSize + index + 1} - ${
                  plan.treatmentName || "Sin nombre"
                }`}
              >
                {/* ✅ Ajustamos la grilla para 5 items (5+5+4+5+5 = 24) */}
                <Row gutter={24} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12} md={5}>
                    <Text type="secondary">Total:</Text>
                    <br />
                    <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                      ${(plan as any).totalAmount?.toFixed(2) || "0.00"}
                    </Title>
                  </Col>
                  <Col xs={24} sm={12} md={5}>
                    <Text type="secondary">Pagado:</Text>
                    <br />
                    <Title level={4} style={{ margin: 0, color: "#52c41a" }}>
                      ${(plan as any).amountPaid?.toFixed(2) || "0.00"}
                    </Title>
                  </Col>

                  {/* ✅ ESTA ES LA NUEVA COLUMNA */}
                  <Col xs={24} sm={12} md={4}>
                    <Text type="secondary">Pendiente:</Text>
                    <br />
                    <Title level={4} style={{ margin: 0, color: "#faad14" }}>
                      ${(plan as any).pendingAmount?.toFixed(2) || "0.00"}
                    </Title>
                  </Col>

                  <Col xs={24} sm={12} md={5}>
                    <Text type="secondary">Inicio:</Text>
                    <br />
                    <Text strong>
                      {(plan as any).startDate
                        ? new Date((plan as any).startDate).toLocaleDateString("es-ES")
                        : "-"}
                    </Text>
                  </Col>
                  <Col xs={24} sm={12} md={5}>
                    <Text type="secondary">Fin:</Text>
                    <br />
                    <Text strong>
                      {(plan as any).endDate
                        ? new Date((plan as any).endDate).toLocaleDateString("es-ES")
                        : "-"}
                    </Text>
                  </Col>
                </Row>

                {plan.variosTreatmens && plan.variosTreatmens.length > 0 ? (
                  <Table
                    dataSource={plan.variosTreatmens}
                    columns={columns}
                    rowKey={(record) => record.treatmentId || record.name}
                    pagination={false}
                    size="small"
                    style={{
                      background: "#fafafa",
                      borderRadius: "6px",
                    }}
                  />
                ) : (
                  <Empty
                    description="Para realizar un pago dirígase a la sección de Estado de Cuenta"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ marginTop: 16 }}
                  />
                )}
              </Card>
            ))}

            {/* Paginación */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 24,
              }}
            >
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={plans.length}
                onChange={handlePageChange}
                showSizeChanger
              />
            </div>
          </>
        ) : (
          <Empty
            description="No hay planes de tratamiento registrados"
            style={{ margin: "40px 0" }}
          />
        )}
      </Card>
    </div>
  );
};

export default Tratamientos;